import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Boxes,
    CheckCircle2,
    ClipboardList,
    MapPin,
    Radio,
    ScanLine,
    ShieldCheck,
    ShoppingBag,
    Sparkles,
    TrendingUp,
    Truck,
    Wifi,
    Zap,
} from 'lucide-react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import {
    cn,
    readLocalStorage,
} from '@/lib/utils';

/* ───────────────────────── data ───────────────────────── */

const TRUSTED_PARTNERS = [
    'Hanseatic Trading Co.',
    'Kora Logistics',
    'Matsuda Industries',
    'Alpine Depot',
    'Sable & Sons',
    'Northwind Supply',
    'Cedarline Partners',
    'Meridian Goods',
    'Obsidian Retail',
];

const BENTO_FEATURES = [
    {
        span: 'lg:col-span-4 lg:row-span-2',
        tone: 'dark',
        eyebrow: 'Inventory',
        title: 'One source of truth for every SKU.',
        body: 'Unify products, groups, categories and units across locations. Stock levels update in real time as orders move.',
        icon: Boxes,
    },
    {
        span: 'lg:col-span-2',
        tone: 'mint',
        eyebrow: 'Quotations',
        title: 'Quote in minutes, not days.',
        body: 'Request, respond and lock in prices without chasing email threads.',
        icon: ClipboardList,
    },
    {
        span: 'lg:col-span-2',
        tone: 'light',
        eyebrow: 'Orders',
        title: 'Two-sided order flow.',
        body: 'Route every purchase and sale through the same audit trail.',
        icon: ShoppingBag,
    },
    {
        span: 'lg:col-span-3',
        tone: 'light',
        eyebrow: 'Logistics',
        title: 'Map, track, hand off.',
        body: 'Find nearby parties on the map, assign deliveries and share proof-of-arrival.',
        icon: MapPin,
    },
    {
        span: 'lg:col-span-3',
        tone: 'blue',
        eyebrow: 'Reporting',
        title: 'Numbers that actually decide things.',
        body: 'Revenue, top movers, late payers, dormant lines — all on one page.',
        icon: BarChart3,
    },
];

const HOW_STEPS = [
    {
        index: '01',
        title: 'Connect your parties',
        body: 'Invite suppliers and customers, or discover verified ones near you.',
    },
    {
        index: '02',
        title: 'Move orders and quotes',
        body: 'Both sides work from the same document. No re-keying, no mismatches.',
    },
    {
        index: '03',
        title: 'Settle and reconcile',
        body: 'Tracked deliveries close the loop. Ledger and reporting stay clean.',
    },
];

const HEADLINE_STATS = [
    { value: '47.2M', label: 'order value processed', sub: 'across 2024' },
    { value: '12,418', label: 'active trading parties', sub: 'verified this quarter' },
    { value: '99.2%', label: 'platform uptime', sub: 'rolling 90-day' },
    { value: '4m 12s', label: 'median quote response', sub: 'supplier-to-buyer' },
];

/* ───────────────────────── greeting ───────────────────────── */

function getTimeGreeting(d: Date): string {
    const h = d.getHours();
    if (h < 5) return 'Still up';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

/* ──────────────────────── mockup preview ──────────────────────── */

function DashboardMockup() {
    return (
        <div
            className={cn(
                'relative w-full rounded-[1.75rem] overflow-hidden',
                'bg-[var(--muted)] ring-1 ring-[var(--foreground)]/5',
                'shadow-[0_40px_80px_-20px_var(--foreground)/0.35,0_20px_40px_-15px_var(--primary)/0.18]',
            )}
            style={{ transform: 'perspective(1800px) rotateY(-4deg) rotateX(4deg)' }}
        >
            {/* mock top bar */}
            <div className="flex items-center gap-2 px-4 h-8 bg-[var(--card)]/90 border-b border-[var(--border)]/70">
                <span className="h-2.5 w-2.5 rounded-full bg-[var(--destructive)]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[var(--warning)]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[var(--success)]/80" />
                <div className="mx-auto h-4 w-40 rounded-md bg-[var(--muted)]" />
            </div>
            <div className="grid grid-cols-[160px_1fr]">
                {/* mock sidebar */}
                <div className="bg-[var(--foreground)] text-[var(--primary-foreground)] p-3 space-y-1.5 min-h-[320px]">
                    <div className="flex items-center pb-3 mb-2 border-b border-[var(--primary-foreground)]/10">
                        <img
                            src="/chanco-horizontal.png"
                            alt="Chanco"
                            className="h-5 w-auto brightness-0 invert"
                        />
                    </div>
                    <div className="h-7 rounded-md bg-[var(--primary)] px-2 flex items-center">
                        <span className="text-[11px] font-semibold">Dashboard</span>
                    </div>
                    {['Related Parties', 'Inventory', 'Orders', 'Quotations', 'Reports'].map((n) => (
                        <div key={n} className="h-7 rounded-md px-2 flex items-center text-[11px] text-[var(--primary-foreground)]/55 hover:text-[var(--primary-foreground)] hover:bg-[var(--primary-foreground)]/5">
                            {n}
                        </div>
                    ))}
                </div>
                {/* mock content */}
                <div className="p-4 space-y-3">
                    {/* stat row */}
                    <div className="grid grid-cols-3 gap-2.5">
                        {[
                            {
                                label: 'Orders', value: '1,284', delta: '+12.5%', tone: 'bg-[var(--primary)]/10 text-[var(--primary)]',
                            },
                            {
                                label: 'Revenue', value: '$48,250', delta: '+8.3%', tone: 'bg-[var(--success)]/10 text-[var(--success)]',
                            },
                            {
                                label: 'Products', value: '356', delta: '+4.1%', tone: 'bg-[var(--foreground)]/5 text-[var(--muted-foreground)]',
                            },
                        ].map((s) => (
                            <div key={s.label} className="rounded-xl bg-[var(--card)] border border-[var(--border)]/70 p-2.5">
                                <div className="text-[9px] uppercase tracking-wider text-[var(--muted-foreground)] font-semibold">{s.label}</div>
                                <div className="text-[15px] font-bold text-[var(--foreground)] leading-tight mt-0.5 tabular-nums">{s.value}</div>
                                <div className={cn('mt-1 inline-flex items-center rounded-full px-1.5 py-[1px] text-[9px] font-semibold', s.tone)}>{s.delta}</div>
                            </div>
                        ))}
                    </div>
                    {/* chart */}
                    <div className="rounded-xl bg-[var(--card)] border border-[var(--border)]/70 p-3">
                        <div className="flex items-center justify-between">
                            <div className="h-3 w-24 rounded bg-[var(--muted)]" />
                            <div className="h-3 w-12 rounded bg-[var(--muted)]" />
                        </div>
                        <svg viewBox="0 0 200 70" className="w-full h-[72px] mt-2">
                            <defs>
                                <linearGradient id="mf" x1="0" x2="0" y1="0" y2="1">
                                    <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
                                    <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <path d="M0,55 C25,40 40,48 60,30 C80,12 100,38 120,28 C140,18 160,34 200,10 L200,70 L0,70 Z" fill="url(#mf)" />
                            <path d="M0,55 C25,40 40,48 60,30 C80,12 100,38 120,28 C140,18 160,34 200,10" fill="none" stroke="var(--primary)" strokeWidth="1.8" />
                            <path d="M0,62 C30,58 60,50 90,52 C130,54 160,44 200,38" fill="none" stroke="var(--success)" strokeWidth="1.6" strokeDasharray="3 3" />
                        </svg>
                    </div>
                    {/* list */}
                    <div className="rounded-xl bg-[var(--card)] border border-[var(--border)]/70 divide-y divide-[var(--border)]">
                        {[
                            { n: 'Obsidian Retail', v: '$2,410', s: 'bg-[var(--success)]' },
                            { n: 'Cedarline Partners', v: '$1,180', s: 'bg-[var(--warning)]' },
                            { n: 'Kora Logistics', v: '$860', s: 'bg-[var(--primary)]' },
                        ].map((r) => (
                            <div key={r.n} className="flex items-center justify-between px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <div className={cn('h-2 w-2 rounded-full', r.s)} />
                                    <span className="text-[11px] text-[var(--muted-foreground)]">{r.n}</span>
                                </div>
                                <span className="text-[11px] font-semibold text-[var(--foreground)] tabular-nums">{r.v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ───────────────────── phone mockup ────────────────────── */

function PhoneMockup() {
    return (
        <div
            className="relative w-[280px] md:w-[300px] aspect-[9/19] mx-auto"
            style={{ transform: 'perspective(1800px) rotateY(6deg) rotateX(2deg)' }}
        >
            {/* device body */}
            <div className="absolute inset-0 rounded-[3rem] bg-[var(--foreground)] p-[10px] shadow-[0_40px_80px_-20px_var(--foreground)/0.55,0_20px_40px_-15px_var(--primary)/0.25]">
                {/* inner frame */}
                <div className="relative h-full w-full rounded-[2.4rem] bg-[var(--card)] overflow-hidden">
                    {/* notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-[var(--foreground)] rounded-b-2xl z-10" />
                    {/* status bar */}
                    <div className="flex items-center justify-between px-6 pt-2.5 text-[10px] font-semibold text-[var(--foreground)] tabular-nums">
                        <span>9:41</span>
                        <div className="flex items-center gap-1">
                            <Wifi className="h-3 w-3" strokeWidth={2.2} />
                            <div className="flex items-center gap-[2px]">
                                <span className="h-2 w-[3px] bg-[var(--foreground)] rounded-sm" />
                                <span className="h-2.5 w-[3px] bg-[var(--foreground)] rounded-sm" />
                                <span className="h-3 w-[3px] bg-[var(--foreground)] rounded-sm" />
                            </div>
                        </div>
                    </div>
                    {/* content */}
                    <div className="px-4 pt-8 pb-4 space-y-3">
                        {/* greeting row */}
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] text-[var(--muted-foreground)] font-medium uppercase tracking-wider">Monday</div>
                                <div className="text-[15px] font-bold text-[var(--foreground)] leading-none mt-0.5">Good morning</div>
                            </div>
                            <div className="relative h-9 w-9 rounded-full bg-[var(--primary)] grid place-items-center text-[var(--primary-foreground)] text-[11px] font-bold">
                                RS
                                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[var(--success)] ring-2 ring-[var(--card)]" />
                            </div>
                        </div>
                        {/* hero stat */}
                        <div className="rounded-2xl bg-[var(--foreground)] text-[var(--primary-foreground)] p-3 overflow-hidden relative">
                            <div aria-hidden className="absolute -top-12 -right-8 h-24 w-24 rounded-full bg-[var(--primary)]/40 blur-2xl" />
                            <div className="relative">
                                <div className="text-[9px] uppercase tracking-[0.15em] text-[var(--primary-foreground)]/55 font-semibold">Revenue today</div>
                                <div className="mt-1 text-[20px] font-bold tabular-nums leading-none">$12,408.70</div>
                                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-[var(--chart-5)] font-semibold">
                                    <TrendingUp className="h-3 w-3" strokeWidth={2.2} />
                                    +8.3%
                                    <span className="text-[var(--primary-foreground)]/40">vs last wk</span>
                                </div>
                                {/* mini sparkline */}
                                <svg viewBox="0 0 200 36" className="mt-2 w-full h-8">
                                    <defs>
                                        <linearGradient id="pf" x1="0" x2="0" y1="0" y2="1">
                                            <stop offset="0%" stopColor="var(--success)" stopOpacity="0.55" />
                                            <stop offset="100%" stopColor="var(--success)" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <path d="M0,28 C20,22 40,26 60,14 C80,4 100,20 120,16 C140,12 160,22 200,6 L200,36 L0,36 Z" fill="url(#pf)" />
                                    <path d="M0,28 C20,22 40,26 60,14 C80,4 100,20 120,16 C140,12 160,22 200,6" fill="none" stroke="var(--chart-5)" strokeWidth="1.6" />
                                </svg>
                            </div>
                        </div>
                        {/* two tiles */}
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-xl bg-[var(--muted)] p-2.5">
                                <div className="text-[9px] uppercase tracking-wider text-[var(--muted-foreground)] font-semibold">Orders</div>
                                <div className="text-[15px] font-bold text-[var(--foreground)] tabular-nums mt-0.5">284</div>
                                <div className="mt-1 text-[9px] font-semibold text-[var(--success)]">+12.5%</div>
                            </div>
                            <div className="rounded-xl bg-[var(--muted)] p-2.5">
                                <div className="text-[9px] uppercase tracking-wider text-[var(--muted-foreground)] font-semibold">Low stock</div>
                                <div className="text-[15px] font-bold text-[var(--foreground)] tabular-nums mt-0.5">9</div>
                                <div className="mt-1 text-[9px] font-semibold text-[var(--destructive)]">needs action</div>
                            </div>
                        </div>
                        {/* order rows */}
                        <div className="rounded-2xl bg-[var(--muted)]/70 ring-1 ring-[var(--border)] divide-y divide-[var(--border)]/70">
                            {[
                                { n: 'Obsidian Retail', v: '$2,410', s: 'bg-[var(--success)]' },
                                { n: 'Kora Logistics', v: '$860', s: 'bg-[var(--primary)]' },
                                { n: 'Cedarline', v: '$1,180', s: 'bg-[var(--warning)]' },
                            ].map((r) => (
                                <div key={r.n} className="flex items-center justify-between px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <span className={cn('h-1.5 w-1.5 rounded-full', r.s)} />
                                        <span className="text-[11px] text-[var(--muted-foreground)]">{r.n}</span>
                                    </div>
                                    <span className="text-[11px] font-semibold text-[var(--foreground)] tabular-nums">{r.v}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* home bar */}
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-24 rounded-full bg-[var(--foreground)]/80" />
                </div>
            </div>
        </div>
    );
}

/* ───────────────── store badges (hand-built) ─────────────── */

function AppStoreBadge() {
    return (
        <button
            type="button"
            aria-label="Download on the App Store"
            className="group inline-flex items-center gap-3 rounded-xl bg-[var(--foreground)] text-[var(--primary-foreground)] px-4 py-2.5 ring-1 ring-[var(--primary-foreground)]/10 hover:bg-[var(--foreground)]/90 transition-colors"
        >
            <svg viewBox="0 0 384 512" className="h-6 w-6" fill="currentColor" aria-hidden>
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.9 77.9c19.9-23.6 18.1-45.1 17.5-52.9-17.6 1-38 12-49.6 25.5-12.8 14.5-20.3 32.4-18.7 51.8 19 1.5 36.4-8.3 50.8-24.4z" />
            </svg>
            <div className="leading-none text-left">
                <div className="text-[9px] opacity-75 font-medium tracking-wider uppercase">Download on the</div>
                <div className="text-[15px] font-semibold tracking-tight mt-0.5">App Store</div>
            </div>
        </button>
    );
}

function PlayStoreBadge() {
    return (
        <button
            type="button"
            aria-label="Get it on Google Play"
            className="group inline-flex items-center gap-3 rounded-xl bg-[var(--foreground)] text-[var(--primary-foreground)] px-4 py-2.5 ring-1 ring-[var(--primary-foreground)]/10 hover:bg-[var(--foreground)]/90 transition-colors"
        >
            <svg viewBox="0 0 512 512" className="h-6 w-6" aria-hidden>
                <path d="M325.3 234.3 104.8 13.8c-3.2-3.2-8.3-3.8-12.2-1.5l234.1 234.1 36.2-36.2-37.6-20.1z" fill="var(--chart-5)" />
                <path d="m88.7 15.7-1.4 1.4C83.8 20.6 82 25.7 82 32v448c0 6.3 1.8 11.4 5.3 14.9l1.4 1.4L320 273.5 264.8 217.3 88.7 15.7z" fill="var(--chart-4)" />
                <path d="m422.2 257.5-73.2-41.4L302.4 262l46.6 45.9 73.5-41.4c20.1-11.3 20.1-29.7-.3-41z" fill="var(--warning)" />
                <path d="m326.1 251.2-237.4 237.4c3.9 2.3 9 1.7 12.2-1.5L363.5 288l-37.4-36.8z" fill="var(--destructive)" />
            </svg>
            <div className="leading-none text-left">
                <div className="text-[9px] opacity-75 font-medium tracking-wider uppercase">Get it on</div>
                <div className="text-[15px] font-semibold tracking-tight mt-0.5">Google Play</div>
            </div>
        </button>
    );
}

/* ───────────────────────── page ───────────────────────── */

export default function Landing() {
    const navigate = useNavigate();
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 30_000);
        return () => window.clearInterval(id);
    }, []);

    const greeting = useMemo(() => getTimeGreeting(now), [now]);

    const handleLogin = useCallback(() => {
        const completed = readLocalStorage('chanco-onboarding-completed');
        navigate(completed === 'true' ? '/login' : '/onboarding');
    }, [navigate]);

    const scrollToFeatures = useCallback(() => {
        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    return (
        <div className="min-h-[100dvh] bg-background text-foreground selection:bg-[var(--primary)]/15">
            {/* ───────────── Navbar ───────────── */}
            <nav className="sticky top-0 z-50 border-b border-border/60 bg-background/75 backdrop-blur-xl">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center">
                        <img
                            src="/chanco-horizontal.png"
                            alt="Chanco"
                            className="h-11 w-auto dark:brightness-0 dark:invert"
                        />
                    </div>
                    <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
                        <button type="button" onClick={scrollToFeatures} className="hover:text-foreground transition-colors">Platform</button>
                        <a href="#numbers" className="hover:text-foreground transition-colors">Numbers</a>
                        <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
                        <a href="#mobile" className="hover:text-foreground transition-colors">Mobile</a>
                    </div>
                    <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <button
                            type="button"
                            onClick={handleLogin}
                            className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5"
                        >
                            Sign in
                        </button>
                        <Button onClick={handleLogin} className="rounded-full px-4">
                            Get started
                            <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </nav>

            {/* ───────────── Hero ───────────── */}
            <section className="relative overflow-hidden">
                {/* ambient — a single faint static grid, no gradient glow */}
                <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 grid-pattern opacity-40 dark:opacity-[0.18]" />

                <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-28">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        {/* copy */}
                        <div className="lg:col-span-5 anim-rise" style={{ ['--rise-index' as string]: 0 }}>
                            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 backdrop-blur px-3 py-1 text-xs font-medium text-muted-foreground">
                                <span className="relative inline-flex h-2 w-2">
                                    <span className="absolute inset-0 rounded-full bg-[var(--success)] anim-pulse-dot" />
                                    <span className="relative inline-block h-2 w-2 rounded-full bg-[var(--success)]" />
                                </span>
                                {greeting}
                                <span className="text-muted-foreground/60">—</span>
                                <span>live in 9 markets</span>
                            </div>

                            <h1 className="mt-5 text-[2.5rem] sm:text-5xl md:text-[3.75rem] leading-[1.02] font-bold tracking-[-0.03em] text-foreground" style={{ textWrap: 'balance' as never }}>
                                Smart inventory.
                                <br />
                                <span className="text-primary">Seamless growth.</span>
                            </h1>

                            <p className="mt-5 text-[1.05rem] leading-relaxed text-muted-foreground max-w-[52ch]">
                                Chanco is the operating layer for supply-side trade.
                                Move orders, quotations and inventory between verified parties
                                without losing a paper trail.
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <Button size="lg" onClick={handleLogin} className="rounded-full px-6 h-11 text-[15px] font-semibold">
                                    Start free
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <button
                                    type="button"
                                    onClick={scrollToFeatures}
                                    className="group inline-flex items-center gap-2 text-[15px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    See the platform
                                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </button>
                            </div>

                            {/* live ticker */}
                            <div className="mt-10 flex items-center gap-5 text-xs text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-4 w-4 text-[var(--success)]" strokeWidth={1.8} />
                                    SOC-2 aligned
                                </div>
                                <div className="h-3 w-px bg-border" />
                                <div className="flex items-center gap-2">
                                    <Radio className="h-4 w-4 text-primary" strokeWidth={1.8} />
                                    Real-time sync
                                </div>
                                <div className="h-3 w-px bg-border" />
                                <div className="flex items-center gap-2 tabular-nums">
                                    <TrendingUp className="h-4 w-4 text-foreground" strokeWidth={1.8} />
                                    47.2M processed
                                </div>
                            </div>
                        </div>

                        {/* mockup */}
                        <div className="lg:col-span-7 relative anim-rise" style={{ ['--rise-index' as string]: 2 }}>
                            <div className="relative anim-float">
                                <DashboardMockup />

                                {/* floating pill — revenue */}
                                <div className="hidden sm:flex absolute -left-4 top-10 items-center gap-3 rounded-2xl bg-card shadow-lg ring-1 ring-border px-4 py-3">
                                    <div className="h-9 w-9 rounded-xl bg-[var(--success)]/12 flex items-center justify-center">
                                        <TrendingUp className="h-4 w-4 text-[var(--success)]" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Revenue today</div>
                                        <div className="text-[15px] font-bold text-foreground tabular-nums">$12,408.70</div>
                                    </div>
                                    <span className="text-[11px] font-semibold text-[var(--success)]">+8.3%</span>
                                </div>

                                {/* floating pill — delivery */}
                                <div className="hidden md:flex absolute -right-2 bottom-16 items-center gap-3 rounded-2xl bg-[var(--foreground)] text-[var(--primary-foreground)] shadow-[0_20px_50px_-15px_var(--foreground)/0.55] px-4 py-3">
                                    <div className="h-9 w-9 rounded-xl bg-[var(--primary-foreground)]/10 flex items-center justify-center">
                                        <Truck className="h-4 w-4 text-[var(--primary-foreground)]" strokeWidth={1.8} />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-[var(--primary-foreground)]/50 font-semibold">En route</div>
                                        <div className="text-[13px] font-semibold tabular-nums">18 deliveries · 3 hubs</div>
                                    </div>
                                </div>

                                {/* floating pill — sparkle */}
                                <div className="hidden lg:flex absolute right-6 -top-3 items-center gap-2 rounded-full bg-card ring-1 ring-border shadow-sm px-3 py-1.5">
                                    <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
                                    <span className="text-xs font-medium text-muted-foreground">Order #A-8129 accepted</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── Trust marquee ───────────── */}
            <section aria-label="Trusted by" className="border-y border-border/60 bg-muted/40">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-6 flex items-center gap-8">
                    <span className="shrink-0 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        Trusted across
                        <br />
                        the supply chain
                    </span>
                    <div className="relative flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
                        <div className="flex gap-12 whitespace-nowrap anim-marquee w-max">
                            {[...TRUSTED_PARTNERS, ...TRUSTED_PARTNERS].map((name, i) => (
                                <span
                                    key={`${name}-${i < TRUSTED_PARTNERS.length ? 'a' : 'b'}`}
                                    className="text-[0.95rem] font-semibold tracking-tight text-muted-foreground/70"
                                >
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── Bento features ───────────── */}
            <section id="features" className="relative py-24 md:py-32">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8">
                    <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
                        <div className="lg:col-span-7">
                            <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">The platform</div>
                            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-[-0.025em] text-foreground max-w-[20ch]">
                                Every moving part of your trade, in one ledger.
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-muted-foreground text-[1rem] leading-relaxed max-w-[52ch]">
                            Built for operators tired of copy-pasting order details
                            between WhatsApp, spreadsheets and their accountant.
                            Chanco keeps both sides of every transaction honest.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-6 gap-5 auto-rows-[minmax(180px,auto)]">
                        {BENTO_FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            const tones: Record<string, string> = {
                                dark: 'bg-[var(--foreground)] text-[var(--primary-foreground)] border-[var(--foreground)]',
                                mint: 'bg-[var(--success)] text-[var(--primary-foreground)] border-[var(--success)]',
                                blue: 'bg-[var(--primary)] text-[var(--primary-foreground)] border-[var(--primary)]',
                                light: 'bg-card text-card-foreground border-border',
                            };
                            const isLight = f.tone === 'light';
                            return (
                                <article
                                    key={f.title}
                                    className={cn(
                                        'relative overflow-hidden rounded-3xl border p-7 md:p-8',
                                        'transition-all duration-300 will-change-transform',
                                        'hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-25px_var(--foreground)/0.25]',
                                        'anim-rise',
                                        tones[f.tone],
                                        f.span,
                                    )}
                                    style={{ ['--rise-index' as string]: i }}
                                >
                                    {f.tone === 'dark' && (
                                        <div aria-hidden className="absolute inset-0 dot-pattern opacity-10" />
                                    )}
                                    <div className="relative flex h-full flex-col">
                                        <div className="flex items-center gap-2">
                                            <div className={cn(
                                                'h-9 w-9 rounded-xl grid place-items-center',
                                                isLight ? 'bg-primary/10 text-primary' : 'bg-[var(--primary-foreground)]/15 text-[var(--primary-foreground)] ring-1 ring-[var(--primary-foreground)]/25',
                                            )}
                                            >
                                                <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                                            </div>
                                            <span className={cn(
                                                'text-[11px] uppercase tracking-[0.18em] font-semibold',
                                                isLight ? 'text-muted-foreground' : 'text-[var(--primary-foreground)]/70',
                                            )}
                                            >
                                                {f.eyebrow}
                                            </span>
                                        </div>
                                        <h3 className={cn(
                                            'mt-5 text-[1.35rem] md:text-[1.5rem] font-bold tracking-[-0.02em] leading-tight',
                                        )}
                                        >
                                            {f.title}
                                        </h3>
                                        <p className={cn(
                                            'mt-2 text-[0.95rem] leading-relaxed',
                                            isLight ? 'text-muted-foreground' : 'text-[var(--primary-foreground)]/75',
                                        )}
                                        >
                                            {f.body}
                                        </p>
                                        {f.tone === 'dark' && (
                                            <div className="mt-auto pt-6">
                                                <div className="h-1.5 w-full rounded-full bg-[var(--primary-foreground)]/5 overflow-hidden">
                                                    <div className="h-full w-1/3 rounded-full bg-primary/70" />
                                                </div>
                                                <div className="mt-3 flex items-center justify-between text-[11px] font-semibold tracking-widest uppercase text-[var(--primary-foreground)]/55">
                                                    <span>Syncing ledger</span>
                                                    <span className="tabular-nums">live</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ───────────── Numbers strip ───────────── */}
            <section id="numbers" className="relative bg-[var(--foreground)] text-[var(--primary-foreground)] overflow-hidden">
                <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 py-20 md:py-24">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 divide-y md:divide-y-0 md:divide-x divide-[var(--primary-foreground)]/10">
                        {HEADLINE_STATS.map((s, i) => (
                            <div
                                key={s.label}
                                className={cn('pt-10 md:pt-0 md:px-8 first:md:pl-0 last:md:pr-0 anim-rise')}
                                style={{ ['--rise-index' as string]: i }}
                            >
                                <div className="text-[clamp(2.25rem,5vw,3.75rem)] font-bold tracking-[-0.03em] leading-none tabular-nums">
                                    {s.value}
                                </div>
                                <div className="mt-3 text-[0.95rem] font-medium text-[var(--primary-foreground)]">{s.label}</div>
                                <div className="mt-0.5 text-xs text-[var(--primary-foreground)]/55 uppercase tracking-[0.15em]">{s.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───────────── How it works ───────────── */}
            <section id="how" className="py-24 md:py-32">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8">
                    <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-[var(--success)] font-semibold">How it works</div>
                            <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-[-0.025em] text-foreground max-w-[22ch]">
                                Three steps from invite to invoice.
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={handleLogin}
                            className="group inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                        >
                            Walk through a demo
                            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </div>

                    <ol className="relative grid md:grid-cols-3 gap-8 md:gap-4">
                        {HOW_STEPS.map((step, i) => (
                            <li
                                key={step.index}
                                className="relative anim-rise"
                                style={{ ['--rise-index' as string]: i }}
                            >
                                {/* connector: trails right from each badge; last fades out */}
                                <div
                                    aria-hidden
                                    className={cn(
                                        'hidden md:block pointer-events-none absolute top-6 left-[calc(3rem+1rem)] h-px',
                                        i < HOW_STEPS.length - 1
                                            ? 'right-[-1rem] bg-border'
                                            : 'right-0 bg-border',
                                    )}
                                />
                                {i === HOW_STEPS.length - 1 && (
                                    <div
                                        aria-hidden
                                        className="hidden md:block pointer-events-none absolute top-[1.375rem] right-0 h-2.5 w-2.5 rounded-full border border-border bg-card"
                                    />
                                )}
                                <div className="flex items-center gap-4">
                                    <div className="relative z-10 grid place-items-center h-12 w-12 rounded-2xl bg-card ring-1 ring-border shadow-sm">
                                        <span className="text-[0.8rem] font-semibold text-primary tabular-nums">{step.index}</span>
                                    </div>
                                    <div className="h-px flex-1 bg-border md:hidden" />
                                </div>
                                <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">{step.title}</h3>
                                <p className="mt-2 text-muted-foreground leading-relaxed max-w-[36ch]">{step.body}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ───────────── Mobile app ───────────── */}
            <section id="mobile" className="pb-24 md:pb-32">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8">
                    <div className="relative overflow-hidden rounded-[2.5rem] bg-card border border-border">
                        <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center p-8 md:p-12 lg:p-16">
                            {/* phone — left on desktop */}
                            <div className="lg:col-span-5 order-2 lg:order-1 relative">
                                <div className="anim-float">
                                    <PhoneMockup />
                                </div>
                                {/* floating notification chips */}
                                <div className="hidden md:flex absolute top-8 -left-2 items-center gap-3 rounded-2xl bg-card shadow-lg ring-1 ring-border px-4 py-3">
                                    <div className="h-9 w-9 rounded-xl bg-primary/10 grid place-items-center">
                                        <Bell className="h-4 w-4 text-primary" strokeWidth={2} />
                                    </div>
                                    <div className="leading-tight">
                                        <div className="text-[11px] font-semibold text-foreground">New quote from Kora Logistics</div>
                                        <div className="text-[10px] text-muted-foreground">tap to review · 14s ago</div>
                                    </div>
                                </div>
                                <div className="hidden md:flex absolute -bottom-4 right-0 items-center gap-2 rounded-full bg-[var(--foreground)] text-[var(--primary-foreground)] px-3 py-1.5 shadow-lg">
                                    <ScanLine className="h-3.5 w-3.5 text-[var(--chart-5)]" strokeWidth={2} />
                                    <span className="text-xs font-medium">Scan-to-add inventory</span>
                                </div>
                            </div>

                            {/* copy — right on desktop */}
                            <div className="lg:col-span-7 order-1 lg:order-2">
                                <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">On the go</div>
                                <h2 className="mt-3 text-3xl md:text-5xl font-bold tracking-[-0.025em] text-foreground max-w-[22ch]">
                                    Your ledger, in your pocket.
                                </h2>
                                <p className="mt-5 text-[1.05rem] leading-relaxed text-muted-foreground max-w-[52ch]">
                                    Approve orders from the warehouse floor, respond
                                    to quotes between meetings, and get pushed the
                                    moment a counterparty moves. Stays in sync with
                                    the web — even on patchy connections.
                                </p>

                                <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-4 max-w-[46rem]">
                                    {[
                                        { icon: Bell, title: 'Real-time alerts', body: 'Status changes, new quotes, delivery updates.' },
                                        { icon: ScanLine, title: 'Scan to add', body: 'Barcode straight into inventory or an order.' },
                                        { icon: Wifi, title: 'Works offline', body: 'Draft orders sync the moment you reconnect.' },
                                        { icon: ShieldCheck, title: 'Biometric login', body: 'Face / Touch ID protects your ledger.' },
                                    ].map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <li key={item.title} className="flex gap-3">
                                                <div className="h-9 w-9 shrink-0 rounded-xl bg-card ring-1 ring-border grid place-items-center shadow-sm">
                                                    <Icon className="h-4 w-4 text-primary" strokeWidth={1.8} />
                                                </div>
                                                <div>
                                                    <div className="text-[0.95rem] font-semibold text-foreground leading-tight">{item.title}</div>
                                                    <div className="mt-0.5 text-sm text-muted-foreground leading-snug">{item.body}</div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mt-9 flex flex-wrap items-center gap-3">
                                    <AppStoreBadge />
                                    <PlayStoreBadge />
                                </div>

                                <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
                                        iOS 16+ · Android 10+
                                    </div>
                                    <div className="h-3 w-px bg-border" />
                                    <span className="tabular-nums">v2.4.1 · 38MB</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── Single testimonial ───────────── */}
            <section className="pb-24 md:pb-32">
                <div className="max-w-[960px] mx-auto px-5 md:px-8">
                    <figure className="relative rounded-[2rem] border border-border bg-card p-8 md:p-12 overflow-hidden">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            <Zap className="h-4 w-4 text-primary" strokeWidth={2} />
                            From the floor
                        </div>
                        <blockquote
                            className={cn(
                                'mt-5 text-[1.25rem] md:text-[1.5rem] leading-[1.35]',
                                'tracking-[-0.015em] text-foreground font-medium max-w-[46ch]',
                            )}
                        >
                            &ldquo;We replaced four WhatsApp groups and a shared sheet
                            with Chanco in one week. Our suppliers confirmed it
                            actually made their life easier — which never happens.&rdquo;
                        </blockquote>
                        <figcaption className="mt-7 flex items-center gap-3 text-sm">
                            <div className="h-9 w-9 rounded-full bg-primary grid place-items-center text-primary-foreground text-xs font-bold">
                                RS
                            </div>
                            <div>
                                <div className="font-semibold text-foreground">Rithika Sapkota</div>
                                <div className="text-muted-foreground">Operations · Cedarline Partners</div>
                            </div>
                        </figcaption>
                    </figure>
                </div>
            </section>

            {/* ───────────── CTA ───────────── */}
            <section className="pb-24">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8">
                    <div className="relative overflow-hidden rounded-[2rem] bg-[var(--foreground)] text-[var(--primary-foreground)] p-10 md:p-14">
                        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <h2 className="text-3xl md:text-5xl font-bold tracking-[-0.03em] leading-[1.05]">
                                    Run your trade on Chanco.
                                    <br />
                                    <span className="text-[var(--primary-foreground)]/55">It takes a morning to migrate.</span>
                                </h2>
                            </div>
                            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
                                <Button
                                    size="lg"
                                    onClick={handleLogin}
                                    className="rounded-full bg-[var(--primary-foreground)] text-[var(--foreground)] hover:bg-[var(--primary-foreground)]/90 px-6 h-12 text-[15px] font-semibold"
                                >
                                    Start free
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <button
                                    type="button"
                                    onClick={scrollToFeatures}
                                    className="inline-flex items-center gap-2 text-[var(--primary-foreground)]/80 hover:text-[var(--primary-foreground)] px-4 h-12 rounded-full border border-[var(--primary-foreground)]/15"
                                >
                                    <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
                                    See the platform
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── Footer ───────────── */}
            <footer className="border-t border-border/70 bg-card">
                <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-10 grid gap-8 md:grid-cols-[auto_1fr_auto] items-center">
                    <div className="flex items-center gap-3">
                        <img
                            src="/chanco-horizontal.png"
                            alt="Chanco"
                            className="h-8 w-auto dark:brightness-0 dark:invert"
                        />
                        <div className="text-xs text-muted-foreground">Smart inventory. Seamless growth.</div>
                    </div>
                    <div className="flex items-center gap-6 md:justify-center text-sm text-muted-foreground">
                        <a className="hover:text-foreground transition-colors" href="#features">Platform</a>
                        <a className="hover:text-foreground transition-colors" href="#numbers">Numbers</a>
                        <a className="hover:text-foreground transition-colors" href="#how">How it works</a>
                        <a className="hover:text-foreground transition-colors" href="#mobile">Mobile</a>
                        <button
                            type="button"
                            className="hover:text-foreground transition-colors"
                            onClick={() => navigate('/login')}
                        >
                            Sign in
                        </button>
                    </div>
                    <p className="text-xs text-muted-foreground md:text-right">
                        ©&nbsp;
                        {now.getFullYear()}
                        &nbsp;Chanco · All rights reserved
                    </p>
                </div>
            </footer>
        </div>
    );
}
